/**
 * This is a TypeScript port of the C++ AnimationsInSequence class from
 * tabanim/public/animation/AnimationsInSequence.h
 *
 * Use this when you want animations to play one after another in a timeline.
 */

import { AnimationGroup } from './AnimationGroup';
import { Animation } from './BaseAnimation';

export class AnimationsInSequence extends AnimationGroup {
  /**
   * The previous fraction value, used to detect direction of travel
   * and handle jumps across segment boundaries.
   * Initialized to -0.1 (outside valid [0,1] range) to indicate "not yet set".
   * Corresponds to C++ m_prevFraction(-0.1f)
   */
  private prevFraction: number;

  /**
   * The starting fraction for each child animation.
   * Parallel to the children array - childrenFractions[i] is the starting fraction for children[i].
   */
  private childrenFractions: number[];

  constructor() {
    super();
    this.prevFraction = -0.1;
    this.childrenFractions = [];
  }

  public getChildrenFractions(): readonly number[] {
    return this.childrenFractions;
  }

  /**
   * Add a child animation that starts updating at the specified fraction, and ends updating
   * at the next higher child's fraction (or 1.0).
   * Replaces any child that has already been added with the same starting fraction.
   */
  public addChild(child: Animation, startingAtFraction: number): [number, boolean] {
    let newChildIndex = -1;
    let addVersusReplace = false; // strack if added or replaced a child

    if (child) {
      // value between 0 and 1
      startingAtFraction = startingAtFraction < 0 ? 0 : startingAtFraction > 1 ? 1 : startingAtFraction;

      for (let i = 0; i < this.children.length; i++) {
        const childFraction = this.childrenFractions[i];

        // if the child fraction is the same as the starting fraction, replace the child
        if (childFraction === startingAtFraction) {
          this.children[i] = child;
          newChildIndex = i;
          addVersusReplace = false;
          break;
        }
        // if the starting fraction is less than the child fraction, insert the child at the index
        else if (startingAtFraction < childFraction) {
          // Insert child at index i, shifts existing children to the right
          this.children.splice(i, 0, child);

          // Insert the fraction for the new child at the same index i, shifts existing fractions to the right
          this.childrenFractions.splice(i, 0, startingAtFraction);
          newChildIndex = i;
          addVersusReplace = true;
          break;
        }
      }
      // starting fraction is greater than all child fractions, add it to the end of the children array
      if (newChildIndex === -1) {
        this.children.push(child);
        this.childrenFractions.push(startingAtFraction);
        newChildIndex = this.children.length - 1;
        addVersusReplace = true;
      }
    }

    return [newChildIndex, addVersusReplace];
  }

  /**
   * Remove the specified child from the sequence.
   * Does nothing if the child is not in the group.
   */
  public removeChild(child: Animation | null): void {
    if (child) {
      // Search for the child in the children array
      const index = this.children.indexOf(child);

      // Check if child was found
      if (index >= 0) {
        // Remove the child from the children array
        this.children.splice(index, 1);

        // Remove the corresponding starting fraction from the fractions array
        this.childrenFractions.splice(index, 1);
      }
    }
  }

  /**
   * Updates the animation sequence based on the current fraction :
   *
   * 1. Finding which child segment contains the current fraction
   * 2. Handling jumps: completing/resetting any animations that were skipped
   * 3. Updating the active child with a rescaled fraction [0, 1] within its segment
   * 4. Tracking state for the next update to detect future jumps
   */
  public update(): void {
    // Initialize segment boundaries
    let prevChildFraction = 0; // start fraction of current segment
    let nextChildFraction = 1; // end fraction of current segment
    let child: Animation | null = null;
    let selectedChildAnimationIndex = -1;

    // Get the current fraction for this animation sequence
    const myFraction = this.getFraction();

    for (let i = 0; i < this.children.length; i++) {
      const childFraction = this.childrenFractions[i];

      // If current fraction is before this child's starting point
      // set child fraction as the end fraction of the current segment
      if (myFraction < childFraction) {
        nextChildFraction = childFraction;
        break;
      }

      // Otherwise, this child is a candidate for the current segment
      // set child fraction as the start fraction of the current segment
      prevChildFraction = childFraction;
      child = this.children[i];
      selectedChildAnimationIndex = i; // index of the child that is the current segment
    }

    // Check if we jumped forward over any animations that need to be completed
    // If previous frame was before current segment start
    if (selectedChildAnimationIndex > 0 && this.prevFraction < prevChildFraction) {
      // At least one previous animation needs to be closed out. Set fraction to 1.0
      for (let i = 0; i < selectedChildAnimationIndex; i++) {
        if (this.childrenFractions[i + 1] > this.prevFraction) {
          this.children[i].setFraction(1.0);
        }
      }
    }

    // Check if we jumped backward over any animations that need to be reset
    // if previous frame was after current segment end
    else if (selectedChildAnimationIndex < this.children.length - 1 && this.prevFraction > nextChildFraction) {
      // Reset fraction to 0.0 on any that would have ended going backwards from
      for (let i = this.children.length - 1; i > selectedChildAnimationIndex; i--) {
        if (this.childrenFractions[i] < this.prevFraction) {
          this.children[i].setFraction(0.0);
        }
      }
    }

    // Update the current child animation with rescaled fraction
    // Rescaled parent's fraction to the child's [0, 1] range
    if (child) {
      const scaledFraction = (myFraction - prevChildFraction) / (nextChildFraction - prevChildFraction);
      child.setFraction(scaledFraction);
    }

    // Remember current fraction for next update
    this.prevFraction = myFraction;
  }

  /**
   * Returns the rescaled fraction within the active child's range
   */
  public fractionWithinCurrentSegment(): number {
    // Initialize segment boundaries
    let prevChildFraction = 0;
    let nextChildFraction = 1;

    // Get the current fraction for this animation sequence
    const myFraction = this.getFraction();

    // Loop through all children to find segment boundaries
    for (let i = 0; i < this.children.length; i++) {
      const childFraction = this.childrenFractions[i];

      // If current fraction is before this child's starting point
      if (myFraction < childFraction) {
        nextChildFraction = childFraction;
        break;
      }

      // Update the previous child fraction as we move forward
      prevChildFraction = childFraction;
    }

    // Handle edge cases and calculate rescaled fraction
    if (myFraction < prevChildFraction) {
      return 0.0;
    } else if (myFraction > nextChildFraction) {
      return 1.0;
    } else {
      return (myFraction - prevChildFraction) / (nextChildFraction - prevChildFraction);
    }
  }
}
